import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { useCreateSkill, useUpdateSkill } from '../../hooks/use-skills';
import { skillFormSchema, SkillFormValues } from '../../schemas/skill-form-schema';
import { isSkillNameTaken } from '../../services/skill.service';
import { Skill } from '../../types/skill.types';

export interface SkillFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  skill?: Skill | null;
  subCategories: { ItemId: string; Title: string }[];
}

/** Adds or edits a skill: its name and the one sub-category it belongs to. */
export const SkillFormDialog = ({
  open,
  onOpenChange,
  skill,
  subCategories,
}: SkillFormDialogProps) => {
  const { t } = useTranslation();
  const isEditMode = Boolean(skill);

  const { mutateAsync: createSkill, isPending: isCreating } = useCreateSkill();
  const { mutateAsync: updateSkill, isPending: isUpdating } = useUpdateSkill();
  const [isChecking, setIsChecking] = useState(false);
  const isSubmitting = isCreating || isUpdating || isChecking;

  const form = useForm<SkillFormValues>({
    resolver: async (data, context, options) => {
      const result = await zodResolver(skillFormSchema)(data, context, options);
      if (result.errors) {
        result.errors = Object.fromEntries(
          Object.entries(result.errors).map(([key, error]) => [
            key,
            { ...error, message: t((error as { message?: string }).message ?? '') },
          ])
        );
      }
      return result;
    },
    defaultValues: { Name: '', SubCategoryId: '' },
  });

  useEffect(() => {
    if (!open) return;
    form.reset({ Name: skill?.Name ?? '', SubCategoryId: skill?.SubCategoryId ?? '' });
  }, [open, skill, form]);

  // A skill whose sub-category was deleted keeps it selectable until HR moves it elsewhere.
  const options =
    skill?.SubCategoryId && !subCategories.some((item) => item.ItemId === skill.SubCategoryId)
      ? [...subCategories, { ItemId: skill.SubCategoryId, Title: t('DELETED_SUB_CATEGORY') }]
      : subCategories;

  const handleSubmit = async (values: SkillFormValues) => {
    const input = {
      Name: values.Name.trim().replace(/\s+/g, ' '),
      SubCategoryId: values.SubCategoryId,
    };
    try {
      setIsChecking(true);
      const taken = await isSkillNameTaken(input.Name, input.SubCategoryId, skill?.ItemId);
      setIsChecking(false);
      if (taken) {
        form.setError('Name', { message: t('SKILL_ALREADY_EXISTS_IN_SUB_CATEGORY') });
        return;
      }
      if (isEditMode && skill) {
        await updateSkill({ itemId: skill.ItemId, input });
      } else {
        await createSkill(input);
      }
      onOpenChange(false);
    } catch {
      // Error toast is already shown by the query/mutation's global error handler.
      setIsChecking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>{isEditMode ? t('EDIT_SKILL') : t('CREATE_SKILL')}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="Name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('SKILL_NAME')} *</FormLabel>
                  <FormControl>
                    <Input placeholder={t('SKILL_NAME')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="SubCategoryId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('SUB_CATEGORY')} *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || undefined}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('SELECT_SKILL_SUB_CATEGORY')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {options.map((subCategory) => (
                        <SelectItem key={subCategory.ItemId} value={subCategory.ItemId}>
                          {subCategory.Title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <DialogCancelButton onClick={() => onOpenChange(false)}>
                {t('CANCEL')}
              </DialogCancelButton>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
                {isEditMode ? t('SAVE_CHANGES') : t('CREATE')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
